import { Link } from "@tanstack/react-router";
import { StatCard } from "@/components/stat-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTlAnalyticsSummary, useTlAtRiskStudents } from "@/hooks/use-tl-exams";
import { useStaffTickets } from "@/hooks/use-tickets";
import { useTLUnreadCount } from "@/hooks/use-tl-dashboard";
import { TicketStatus } from "@/lib/api/tickets";
import {
  Users,
  GraduationCap,
  ClipboardCheck,
  AlertTriangle,
  LifeBuoy,
  MessageCircle,
} from "lucide-react";

export function DashboardKpiGrid() {
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useTlAnalyticsSummary({});

  const {
    data: atRiskData,
    isLoading: atRiskLoading,
    isError: atRiskError,
  } = useTlAtRiskStudents({ minRiskFlags: 1, pageSize: 1 });

  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    isError: ticketsError,
  } = useStaffTickets({ status: TicketStatus.Open, pageSize: 1 });

  const { data: unreadCount, isLoading: unreadLoading, isError: unreadError } = useTLUnreadCount();

  // Compute pass rate safely: with no completed attempts, render "—"
  const passRateValue = (() => {
    if (summaryLoading) {
      return <Skeleton className="h-6 w-12 my-0.5" />;
    }
    if (summaryError || !summary) {
      return "—";
    }
    const attempts = summary.passed + summary.failed;
    if (attempts === 0) {
      return "—";
    }
    return `${Math.round((summary.passed / attempts) * 100)}%`;
  })();

  const passRateSub = (() => {
    if (summaryError) {
      return <span className="text-destructive">Unavailable</span>;
    }
    if (summaryLoading) {
      return <Skeleton className="h-3 w-16 mt-0.5" />;
    }
    if (!summary || summary.passed + summary.failed === 0) {
      return "No completed attempts";
    }
    return `${summary.passed} passed · ${summary.failed} failed`;
  })();

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {/* 1. Students */}
      <Link
        to="/team-leader/students"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="Students"
          value={
            summaryLoading ? (
              <Skeleton className="h-6 w-12 my-0.5" />
            ) : summaryError || !summary ? (
              "—"
            ) : (
              summary.totalStudents.toLocaleString()
            )
          }
          sub={
            summaryError ? (
              <span className="text-destructive">Unavailable</span>
            ) : summaryLoading ? (
              <Skeleton className="h-3 w-16 mt-0.5" />
            ) : (
              `${summary?.activeAttempts ?? 0} active attempts`
            )
          }
          icon={<Users className="h-5 w-5" aria-hidden="true" />}
          color="primary"
        />
      </Link>

      {/* 2. Pass Rate */}
      <Link
        to="/team-leader/exams"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="Pass Rate"
          value={passRateValue}
          sub={passRateSub}
          icon={<GraduationCap className="h-5 w-5" aria-hidden="true" />}
          color="green"
        />
      </Link>

      {/* 3. To Grade */}
      <Link
        to="/team-leader/exams/review"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="To Grade"
          value={
            summaryLoading ? (
              <Skeleton className="h-6 w-12 my-0.5" />
            ) : summaryError || !summary ? (
              "—"
            ) : (
              summary.pendingGrading.toLocaleString()
            )
          }
          sub={
            summaryError ? (
              <span className="text-destructive">Unavailable</span>
            ) : summaryLoading ? (
              <Skeleton className="h-3 w-16 mt-0.5" />
            ) : (
              `${summary?.essayPending ?? 0} essays pending`
            )
          }
          icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
          color="amber"
        />
      </Link>

      {/* 4. At Risk */}
      <Link
        to="/team-leader/exams/at-risk"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="At Risk"
          value={
            atRiskLoading ? (
              <Skeleton className="h-6 w-12 my-0.5" />
            ) : atRiskError ? (
              "—"
            ) : (
              (atRiskData?.totalCount ?? 0).toLocaleString()
            )
          }
          sub={
            atRiskError ? (
              <span className="text-destructive">Unavailable</span>
            ) : atRiskLoading ? (
              <Skeleton className="h-3 w-16 mt-0.5" />
            ) : (
              "Need attention"
            )
          }
          icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
          color="red"
        />
      </Link>

      {/* 5. Open Tickets */}
      <Link
        to="/team-leader/tickets"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="Open Tickets"
          value={
            ticketsLoading ? (
              <Skeleton className="h-6 w-12 my-0.5" />
            ) : ticketsError ? (
              "—"
            ) : (
              (ticketsData?.totalCount ?? 0).toLocaleString()
            )
          }
          sub={
            ticketsError ? (
              <span className="text-destructive">Unavailable</span>
            ) : ticketsLoading ? (
              <Skeleton className="h-3 w-16 mt-0.5" />
            ) : (
              "Support queue"
            )
          }
          icon={<LifeBuoy className="h-5 w-5" aria-hidden="true" />}
          color="secondary"
        />
      </Link>

      {/* 6. Unread */}
      <Link
        to="/messages"
        className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <StatCard
          label="Unread"
          value={
            unreadLoading ? (
              <Skeleton className="h-6 w-12 my-0.5" />
            ) : unreadError ? (
              "—"
            ) : (
              (unreadCount ?? 0).toLocaleString()
            )
          }
          sub={
            unreadError ? (
              <span className="text-destructive">Unavailable</span>
            ) : unreadLoading ? (
              <Skeleton className="h-3 w-16 mt-0.5" />
            ) : (
              "Messages"
            )
          }
          icon={<MessageCircle className="h-5 w-5" aria-hidden="true" />}
          color="primary"
        />
      </Link>
    </div>
  );
}
